import styled from 'styled-components'

import { Layout, Link, Text } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { TrackingProvider } from '../../../domain/Tracking'
import { formatTrackingProviderName } from '../../../presenters/preferredTrackingMethod'
import ImageMobile from '../../assets/tracking-onboarding-finalize-connection-mobile.png'
import { useTrackingOnboardingContext } from './TrackingOnboardingContext'

const StyledListItem = styled.li`
  padding-bottom: ${getToken('spacing-2')};
  list-style: decimal;
`

const MOBILE_APP_LINKS = {
  [TrackingProvider.P44]: {
    android: 'https://play.google.com/store/apps/details?id=com.project44.driview&hl=en',
    ios: 'https://apps.apple.com/us/app/driveview-by-project44/id1439370774',
  },
  [TrackingProvider.TT]: {
    android: 'https://play.google.com/store/apps/details?id=com.salebug.truckstop&hl=en',
    ios: 'https://apps.apple.com/us/app/trucker-tools/id362413088',
  },
  [TrackingProvider.Loadsmart]: {
    android: 'https://play.google.com/store/apps/details?id=com.loadsmart.liveloads&hl=en',
    ios: 'https://apps.apple.com/br/app/loadsmart-loads/id1256783942',
  },
}

function getMobileAppLink(platform: 'android' | 'ios', provider?: TrackingProvider) {
  if (!provider) return

  return MOBILE_APP_LINKS[provider][platform]
}

export type MobileFinalizeConnectionContentProps = {
  readonly selectedProvider?: TrackingProvider
}

export default function MobileFinalizeConnectionContent({
  selectedProvider,
}: MobileFinalizeConnectionContentProps) {
  const { hasPreviousTracking } = useTrackingOnboardingContext()

  if (hasPreviousTracking) {
    return (
      <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
        <Layout.Stack gap="spacing-4">
          <Text variant="body-md-bold">Mobile tracking</Text>
          <Layout.Stack gap="spacing-6">
            <Text variant="body-md">
              You're about to confirm your preferred connection as{' '}
              {formatTrackingProviderName(selectedProvider)} Mobile.
            </Text>
            <Text variant="body-md">You can update this choice anytime via the tracking card.</Text>
          </Layout.Stack>
        </Layout.Stack>
        <img src={ImageMobile} alt="" width={210} height={220} />
      </Layout.Group>
    )
  }

  return (
    <Layout.Group style={{ flexWrap: 'nowrap' }} gap="spacing-12">
      <Layout.Stack gap="spacing-4">
        <Text variant="body-md-bold">Install the mobile app for drivers</Text>
        <Text variant="body-md">
          To start tracking through mobile, your driver needs to download the chosen app on the{' '}
          <Link
            href={getMobileAppLink('android', selectedProvider)}
            target="_blank"
            aria-label="play store app link"
          >
            Play Store
          </Link>
          or
          <Link
            href={getMobileAppLink('ios', selectedProvider)}
            target="_blank"
            aria-label="app store app link"
          >
            App Store
          </Link>
          to ensure every load has tracking you need to follow these steps:
        </Text>
        <Layout.Group paddingL="spacing-4">
          <Text variant="body-md">
            <ol>
              <StyledListItem>
                You have to add the mobile phone number to every load you want to track.
              </StyledListItem>
              <StyledListItem>
                If the driver does not have the app installed, they will receive a text message
                asking to download and install it.
              </StyledListItem>
              <StyledListItem>
                Make sure your drivers have allowed the chosen app to share the locations even with
                the app on the background.
              </StyledListItem>
            </ol>
          </Text>
        </Layout.Group>
      </Layout.Stack>
      <img src={ImageMobile} alt="" width={210} height={220} />
    </Layout.Group>
  )
}
